import $ from 'jquery';
import { Observable, Subject, Subscription, of, UnaryFunction } from 'rxjs';
import { catchError, throttleTime } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl, Validators } from '@angular/forms';
import {
  Component,
  AfterViewInit,
  ElementRef,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { CustomValidators } from '../../services/custom-validators';
import { FlashMessage } from '../../services/flash-message';
import { HIDE_FLASH_TIME } from '../../layout/flash-message.component';
import { ShareTrackingService } from './share-tracking.service';
import { copyValueToClipboard } from '../../lib/utils';

export interface IShareTrackingParams {
  email_addresses: string[];
  dispatch_id: number;
}

export interface IDispatchShareInfo {
  id: number;
  orderNumber: string;
  trackingURL: string;
}

@Component({
  selector: 'share-tracking-modal',
  styleUrls: ['./share-tracking-modal.component.scss'],
  templateUrl: './share-tracking-modal.component.html',
  providers: [CustomValidators, ShareTrackingService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShareTrackingURLModalComponent implements AfterViewInit, OnDestroy {
  @Input() public isPublicUser: boolean = false;
  @Output() public publicShare: EventEmitter<string> = new EventEmitter<string>();
  public emailControl: FormControl = new FormControl('', [Validators.required, this.customValidators.emailFormat()]);
  public dispatch: IDispatchShareInfo;
  public isSubmitted: boolean = false;
  public sendingEmail: boolean = false;
  private el: HTMLElement;
  private $modal: any;
  private copyTrackingURL$: Subject<undefined> = new Subject<undefined>();
  private copySub: Subscription = new Subscription();

  constructor(
    el: ElementRef,
    private customValidators: CustomValidators,
    private shareTrackingService: ShareTrackingService,
    private flashMessage: FlashMessage,
    private cdr: ChangeDetectorRef,
  ) {
    this.el = el.nativeElement;
  }

  public ngAfterViewInit(): void {
    this.$modal = $(this.el.querySelector('.ui.modal'));
    this.$modal.modal({
      onHide: this.resetFormControl.bind(this),
    });
    this.copySub = this.copyTrackingURL$.pipe(throttleTime(HIDE_FLASH_TIME)).subscribe(this.copyTrackingURL.bind(this));
  }

  public ngOnDestroy(): void {
    this.copySub.unsubscribe();
    this.$modal.modal('hide');
    this.$modal.modal('destroy');
    this.$modal.remove();
  }

  public show(dispatch: IDispatchShareInfo): void {
    this.dispatch = dispatch;
    this.cdr.markForCheck();
    this.$modal.modal('show');
  }

  public shareDetail(event: Event): void {
    event.preventDefault(); // to avoid closing of semantic modal on button click : Default Semantic Modal Behaviour
    this.isSubmitted = true;
    if (this.emailControl.invalid) return;
    this.sendingEmail = true;
    const shareTrackingParams: IShareTrackingParams = {
      email_addresses: [this.emailControl.value],
      dispatch_id: this.dispatch.id,
    };
    this.cdr.markForCheck();
    if (!this.isPublicUser) {
      this.shareTrackingService.sendTrackingEmail(shareTrackingParams)
        .pipe(this.handleEmailSentError())
        .subscribe((emailSent: boolean) => {
          this.handleEmailSentResponse(emailSent);
        })
        ;
    } else {
      this.publicShare.emit(this.emailControl.value);
    }

  }

  public handleEmailSentError<T>(): UnaryFunction<Observable<T>, any> {
    return catchError((resError: HttpErrorResponse, obs: Observable<any>) => {
      if (resError.error.error.indexOf('bad email') !== -1) {
        this.emailControl.setErrors({ deliverable: true });
      }
      return of(false);
    });
  }

  public handleEmailSentResponse(emailSent: boolean): void {
    if (emailSent) {
      this.showSharedSuccessfullyMessage(this.dispatch.orderNumber, this.emailControl.value);
      this.resetFormControl();
    }
    this.sendingEmail = false;
    this.cdr.markForCheck();
  }

  public onCopyClick(event: Event): void {
    event.preventDefault(); // to avoid closing of semantic modal on button click : Default Semantic Modal Behaviour
    this.copyTrackingURL$.next();
  }

  private showSharedSuccessfullyMessage(orderNumber: string, email: string): void {
    this.flashMessage.show(
      $localize`Tracking link for Dispatch #${orderNumber} has been shared with ${email}.`,
      { type: 'success' });
  }

  private copyTrackingURL(): void {
    copyValueToClipboard(this.dispatch.trackingURL);
    this.flashMessage.show($localize`Successfully copied to clipboard.`, { type: 'success' });
  }

  private resetFormControl(): void {
    this.isSubmitted = false;
    this.emailControl.reset();
  }

}
